import React, { useState, useRef, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { Col, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { companySelector } from 'slices/company';
import { useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
//icons
import { AiFillDelete, AiFillEdit } from "react-icons/ai";
import { BsTranslate } from "react-icons/bs";
//api
import { createMenu } from 'api/company';
//custom-hooks
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
//components
import Title from 'components/backoffice/texts/title';
import MenuForm from 'components/backoffice/forms/menuForm';
import MenuModal from './modal/menuModal';
import Button from 'components/backoffice/buttonCustom';
import TranslationModal from './modal/translationModal';

const ItemType = 'MENU_ITEM';

const Menu = () => {
  const { t } = useTranslation(['backoffice']);
  const { menus } = useSelector(companySelector);
  const [menuItems, setMenuItems] = useState([]);
  const [newItem, setNewItem] = useState({ title: '', url: '', keyword: '', list: '', parentId: null });
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editedItem, setEditedItem] = useState({});
  const [editedItemParentId, setEditedItemParentId] = useState(null);
  const { setTitle } = useOutletContext()
  const { assignedLibrary } = useAssignedLibrary();
  const [translateModalOpen, setTranslateModalOpen] = useState(false);
  const [translatedItem, setTranslatedItem] = useState({ id: null, text: '' });

  const openTranslateModal = (item) => {
    setTranslatedItem({
      id: item.id,
      text: item.text,
      text_en: item.text_en,
      text_fr: item.text_fr,
      text_eu: item.text_eu,
      text_ca: item.text_ca,
      text_gl: item.text_gl
    });
    setTranslateModalOpen(true);
  };

  useEffect(() => {
    if (menus && menus.length > 0) {
      setMenuItems(menus);
    }
  }, [menus]);

  useEffect(() => {
    setTitle(t('menu'))
    return () => {
      setTitle('');
    };
  }, [setTitle])

  const handleAddItem = () => {
    const newMenuItem = {
      id: Date.now(),
      text: newItem.title,
      url: newItem.list ? {
        pathname: `/${assignedLibrary}/lista/products_by_list`,
        search: `?title=${encodeURIComponent(newItem.list?.name)}`,
        state: { list: newItem.list?.id }
      } : newItem.url,
      keyword: newItem.keyword ? newItem.keyword : '',
      submenu: [],
    };
    if (newItem.parentId) {
      let updatedMenuItems = menuItems.map((item) => {
        if (item.id === parseInt(newItem.parentId)) {
          return { ...item, submenu: [...item.submenu, newMenuItem] };
        }
        return item;
      });
      setMenuItems(updatedMenuItems);
    } else {
      setMenuItems((prev) => [...prev, newMenuItem]);
    }
    setNewItem({ title: '', url: '', keyword: '', list: '', parentId: null });
    setSaveButtonDisabled(false);
  };

  const isFormValid = () => {
    return newItem.title;
  };

  const handleSaveMenu = async () => {
    try {
      const payload = { menu_json: JSON.parse(JSON.stringify(menuItems)) };
      const response = await createMenu(payload);
      if (response.status === 200) {
        toast.success(t('guardadocorrectamente'));
        setSaveButtonDisabled(true);
      } else {
        toast.error(`${t('error')}: ${t('mensajeerror')}`, { theme: 'colored' });
      }
    } catch (error) {
      toast.error(`${t('error')}: ${error.message}`, { theme: 'colored' });
    }
  };

  const moveMenuItem = (dragIndex, hoverIndex, parentIndex) => {
    const newMenuItems = JSON.parse(JSON.stringify(menuItems));
    const draggedItem = parentIndex !== undefined ? newMenuItems[parentIndex].submenu.splice(dragIndex, 1)[0] : newMenuItems.splice(dragIndex, 1)[0];
    parentIndex !== undefined ? newMenuItems[parentIndex].submenu.splice(hoverIndex, 0, draggedItem) : newMenuItems.splice(hoverIndex, 0, draggedItem);
    setTimeout(() => {
      setMenuItems(newMenuItems);
    }, 0);
    setSaveButtonDisabled(false);
  };

  const convertToSubMenu = (dragIndex, hoverIndex, parentIndex) => {
    const newMenuItems = JSON.parse(JSON.stringify(menuItems));
    const draggedItem = parentIndex !== undefined ? newMenuItems[parentIndex].submenu.splice(dragIndex, 1)[0] : newMenuItems.splice(dragIndex, 1)[0];
    newMenuItems[hoverIndex].submenu.push(draggedItem);
    setTimeout(() => {
      setMenuItems(newMenuItems);
    }, 0);
    setSaveButtonDisabled(false);
  };

  const removeFromSubMenu = (parentIndex, subIndex) => {
    const newMenuItems = JSON.parse(JSON.stringify(menuItems));
    const draggedItem = newMenuItems[parentIndex].submenu.splice(subIndex, 1)[0];
    newMenuItems.splice(parentIndex + 1, 0, draggedItem);
    setTimeout(() => {
      setMenuItems(newMenuItems);
    }, 0); setSaveButtonDisabled(false);
  };

  const moveSubMenuItem = (parentIndex, dragIndex, hoverIndex) => {
    const newMenuItems = JSON.parse(JSON.stringify(menuItems));
    const draggedItem = newMenuItems[parentIndex].submenu.splice(dragIndex, 1)[0];
    newMenuItems[parentIndex].submenu.splice(hoverIndex, 0, draggedItem);
    setTimeout(() => {
      setMenuItems(newMenuItems);
    }, 0); setSaveButtonDisabled(false);
  };

  const handleEditItem = () => {
    const updatedMenuItems = JSON.parse(JSON.stringify(menuItems));
    if (editedItem && editedItem.id !== undefined) {
      if (editedItemParentId !== null) {
        const parentIndex = updatedMenuItems.findIndex(item => item.id === editedItemParentId);
        if (parentIndex !== -1 && updatedMenuItems[parentIndex].submenu) {
          const submenuIndex = updatedMenuItems[parentIndex].submenu.findIndex(subitem => subitem && subitem.id === editedItem.id);
          if (submenuIndex !== -1) {
            updatedMenuItems[parentIndex].submenu[submenuIndex] = { ...updatedMenuItems[parentIndex].submenu[submenuIndex], text: editedItem.text, url: editedItem.url, keyword: editedItem.keyword };
          }
        }
      } else {
        const index = updatedMenuItems.findIndex(item => item.id === editedItem.id);
        if (index !== -1) {
          updatedMenuItems[index] = { ...updatedMenuItems[index], text: editedItem.text, url: editedItem.url, keyword: editedItem.keyword };
        }
      }
      setMenuItems(updatedMenuItems);
      setEditModalOpen(false);
      setSaveButtonDisabled(false);
    } else {
      console.error(editedItem);
    }
  };

  const handleTranslateItem = () => {
    const updatedMenuItems = JSON.parse(JSON.stringify(menuItems));
    if (translatedItem && translatedItem.id !== undefined) {
      const updateItem = (items) => {
        for (let i = 0; i < items.length; i++) {
          if (items[i].id === translatedItem.id) {
            items[i] = {
              ...items[i],
              text_en: translatedItem.text_en,
              text_fr: translatedItem.text_fr,
              text_eu: translatedItem.text_eu,
              text_ca: translatedItem.text_ca,
              text_gl: translatedItem.text_gl
            };
            return true;
          }
          if (items[i].submenu) {
            if (updateItem(items[i].submenu)) return true;
          }
        }
        return false;
      };
      if (updateItem(updatedMenuItems)) {
        setMenuItems(updatedMenuItems);
        setTranslateModalOpen(false);
        setSaveButtonDisabled(false);
      } else {
        console.error("No se encontró el ítem a actualizar.");
      }
    }
  };

  const handleDeleteItem = (id, parentIndex) => {
    let updatedMenuItems = JSON.parse(JSON.stringify(menuItems));
    if (parentIndex !== undefined && menuItems[parentIndex].submenu) {
      updatedMenuItems[parentIndex].submenu = updatedMenuItems[parentIndex].submenu.filter(
        (item) => item && item.id !== id
      );
    } else {
      updatedMenuItems = menuItems.filter(item => item && item.id !== id);
    }
    setMenuItems(updatedMenuItems);
    setSaveButtonDisabled(false);
  };

  const handleClose = () => {
    setEditModalOpen(false)
    setTitle(t('menu'))
  }

  const MenuItem = ({ index, item, parentIndex }) => {
    const { id, text, url, keyword, submenu = [] } = item;
    const ref = useRef(null);
    const [, drop] = useDrop({
      accept: ItemType,
      hover(item, monitor) {
        const dragIndex = item.index;
        const hoverIndex = index;
        const parentIdx = item.parentIndex;

        if (!ref.current || !monitor || !monitor.isOver()) {
          return;
        }

        const hoverBoundingRect = ref.current.getBoundingClientRect();
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;

        if (dragIndex === hoverIndex && parentIdx === parentIndex) {
          return;
        }

        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return;
        }

        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return;
        }

        if (parentIdx === undefined) {
          if (parentIndex === undefined) {
            moveMenuItem(dragIndex, hoverIndex);
          } else {
            moveSubMenuItem(parentIndex, dragIndex, hoverIndex);
          }
          item.index = hoverIndex;
        } else if (parentIdx === parentIndex) {
          moveSubMenuItem(parentIndex, dragIndex, hoverIndex);
          item.index = hoverIndex;
        }
      },

      drop(item, monitor) {
        if (parentIndex !== undefined) {
          return;
        }

        if (!ref.current) {
          return;
        }

        const dragIndex = item.index;
        const hoverIndex = index;
        const parentIdx = item.parentIndex;

        if (parentIdx !== undefined && parentIdx !== hoverIndex) {
          removeFromSubMenu(parentIdx, dragIndex);
          item.index = menuItems.length - 1;
          item.parentIndex = undefined;
          return;
        } else if (dragIndex !== hoverIndex && parentIdx === undefined) {
          if (menuItems[dragIndex] && menuItems[dragIndex].submenu && menuItems[dragIndex].submenu.length === 0) {
            if (dragIndex < hoverIndex) {
              convertToSubMenu(dragIndex, hoverIndex - 1, parentIndex);
              item.index = hoverIndex - 1;
            } else {
              convertToSubMenu(dragIndex, hoverIndex, parentIndex);
              item.index = hoverIndex;
            }
          } else {
          }
        }
      },
    });

    const [{ isDragging }, drag] = useDrag({
      type: ItemType,
      item: { type: ItemType, id, index, parentIndex },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    drag(drop(ref));

    const openEditModal = () => {
      setEditedItem({ id, text, url, keyword });
      setEditedItemParentId(parentIndex !== undefined ? menuItems[parentIndex].id : null);
      setEditModalOpen(true);
    }

    return (
      <div ref={ref} className={`menu-item ${isDragging ? 'dragging' : ''}`}>
        <Row >
          <Col sm={9}>
            {text}
            <div className="submenu">
              {submenu.map((subItem, subIndex) => (
                subItem && (
                  <MenuItem
                    key={subItem.id}
                    index={subIndex}
                    item={subItem}
                    parentIndex={index}
                  />
                )
              ))}
            </div>
          </Col>
          <Col sm={3} className="d-flex justify-content-end align-items-start">
            <div className="actions">
              <BsTranslate size={25} color="#fff" className="me-4 cursor-pointer" onClick={() => openTranslateModal(item)} />
              <AiFillEdit size={25} color="#fff" className="me-4 cursor-pointer" onClick={openEditModal} />
              <AiFillDelete size={25} color="#fff" className="cursor-pointer" onClick={() => handleDeleteItem(id, parentIndex)} />
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <>
      <Title text={t('menu')} />
      <MenuForm
        newItem={newItem}
        setNewItem={setNewItem}
        menuItems={menuItems}
        handleAddItem={handleAddItem}
        isFormValid={isFormValid}
      />
      <DndProvider backend={HTML5Backend}>
        <div className="menu-container">
          {Array.isArray(menuItems) && menuItems?.length === 0
            ? <div className="no-menu-items">{t('addMenu')}</div>
            : Array.isArray(menuItems) && menuItems?.map((item, index) => (
              <MenuItem
                key={item.id}
                index={index}
                item={item}
              />
            ))}
          <Row className="my-4">
            <Col sm={12} className="text-end mt-3">
              <Button
                color={'primary'}
                onClick={handleSaveMenu}
                loading={saveButtonDisabled}
              >
                {t('updateEverything')}
              </Button>
            </Col>
          </Row>
        </div>
      </DndProvider>
      <MenuModal
        show={editModalOpen}
        onHide={handleClose}
        editedItem={editedItem}
        setEditedItem={setEditedItem}
        handleEditItem={handleEditItem}
      />
      <TranslationModal
        show={translateModalOpen}
        onHide={() => setTranslateModalOpen(false)}
        translatedItem={translatedItem}
        setTranslatedItem={setTranslatedItem}
        handleTranslateItem={handleTranslateItem}
      />
    </>
  );
};

export default Menu;